/**
 * Filter state in the URL (DS foundation). A small, router-agnostic adapter: give it the defaults and a
 * `[params, setParams]` pair (react-router's `useSearchParams()` fits as-is) and it returns typed values, a
 * `set(patch)` that writes only non-default keys, `reset()`, and the active-filter count FilterToolbar shows.
 *
 * Encoding: string -> as is; string[] -> comma-joined; boolean -> "1" (absent = false); number -> decimal.
 * A key equal to its default is removed, so a clean list has a clean URL and links stay short and shareable.
 * Any change that isn't itself a page change resets `page` (results moved; page 3 of the old list is meaningless).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type FilterValue = string | readonly string[] | boolean | number;
export type FilterDefaults = Record<string, FilterValue>;
type Widen<T> = T extends boolean ? boolean : T extends number ? number : T extends readonly string[] ? string[] : T extends string ? string : never;
export type FilterValues<D extends FilterDefaults> = { [K in keyof D]: Widen<D[K]> };
export type SetSearchParams = (next: (prev: URLSearchParams) => URLSearchParams, opts?: { replace?: boolean }) => void;

function decode(raw: string | null, def: FilterValue): FilterValue {
  if (raw === null) return Array.isArray(def) ? [...def] : def;
  if (typeof def === "boolean") return raw === "1" || raw === "true";
  if (typeof def === "number") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : def;
  }
  if (Array.isArray(def)) return raw.split(",").map((s) => s.trim()).filter(Boolean);
  return raw;
}
function encode(v: FilterValue): string | null {
  if (typeof v === "boolean") return v ? "1" : null;
  if (typeof v === "number") return String(v);
  if (Array.isArray(v)) return v.length ? v.join(",") : null;
  return v as string;
}
const same = (a: FilterValue, b: FilterValue) => (Array.isArray(a) && Array.isArray(b) ? a.length === b.length && a.every((x, i) => x === b[i]) : a === b);

/** Typed values from the URL; missing or malformed keys fall back to their defaults. */
export function readFilters<D extends FilterDefaults>(params: URLSearchParams, defaults: D): FilterValues<D> {
  const out: Record<string, FilterValue> = {};
  for (const [k, def] of Object.entries(defaults)) out[k] = decode(params.get(k), def);
  return out as FilterValues<D>;
}

/** Next URL params after applying `patch`: default values are dropped; unrelated params are kept. */
export function writeFilters<D extends FilterDefaults>(prev: URLSearchParams, patch: Partial<FilterValues<D>>, defaults: D, resetKeys: readonly string[] = ["page"]): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const [k, v] of Object.entries(patch) as [string, FilterValue | undefined][]) {
    if (v === undefined) continue;
    const def = defaults[k];
    const enc = encode(v);
    if (enc === null || enc === "" || (def !== undefined && same(v, def))) next.delete(k);
    else next.set(k, enc);
  }
  for (const r of resetKeys) if (!(r in patch)) next.delete(r);
  return next;
}

/** How many filters differ from their defaults (search counts; paging and sorting don't). */
export function activeFilterCount<D extends FilterDefaults>(values: FilterValues<D>, defaults: D, ignore: readonly string[] = ["page", "sort", "dir", "view"]): number {
  let n = 0;
  for (const [k, def] of Object.entries(defaults)) if (!ignore.includes(k) && !same(values[k] as FilterValue, def)) n++;
  return n;
}

/** The full query for the API: every key, defaults included (stable cache keys, explicit server contract). */
export function filterQuery<D extends FilterDefaults>(values: FilterValues<D>): URLSearchParams {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(values) as [string, FilterValue][]) q.set(k, encode(v) ?? (typeof v === "boolean" ? "0" : ""));
  return q;
}

export type UrlFilters<D extends FilterDefaults> = {
  values: FilterValues<D>;
  set: (patch: Partial<FilterValues<D>>) => void;
  reset: () => void;
  /** Filters away from their default (excludes page, sort, dir, view). */
  active: number;
  anyActive: boolean;
  /** Every key with defaults, for the API call. */
  query: URLSearchParams;
};

/**
 * Filters that live in the URL. `set` replaces the history entry (filtering isn't navigation; Back leaves the page).
 * Usage: `const f = useUrlFilters({ q: "", kind: "all", page: 0 }, useSearchParams());`
 */
export function useUrlFilters<D extends FilterDefaults>(defaults: D, [params, setParams]: readonly [URLSearchParams, SetSearchParams], opts: { ignoreForActive?: readonly string[]; resetKeys?: readonly string[] } = {}): UrlFilters<D> {
  const defRef = useRef(defaults);
  defRef.current = defaults;
  const key = params.toString();
  // Keyed on the serialized params, not the object identity (routers hand out a new object per render).
  const values = useMemo(() => readFilters(params, defRef.current), [key]);
  const { ignoreForActive, resetKeys } = opts;
  const set = useCallback((patch: Partial<FilterValues<D>>) => setParams((p) => writeFilters(p, patch, defRef.current, resetKeys), { replace: true }), [setParams, resetKeys]);
  const reset = useCallback(() => setParams((p) => {
    const next = new URLSearchParams(p);
    for (const k of Object.keys(defRef.current)) next.delete(k);
    return next;
  }, { replace: true }), [setParams]);
  const active = activeFilterCount(values, defaults, ignoreForActive);
  return { values, set, reset, active, anyActive: active > 0, query: filterQuery(values) };
}

/**
 * Debounced text bound to a committed value (search box -> URL). Typing updates instantly; `commit` runs after
 * `ms` of quiet. When the committed value changes from outside (Reset, Back), the text follows it.
 */
export function useDebouncedText(committed: string, commit: (v: string) => void, ms = 250): [string, (v: string) => void] {
  const [text, setText] = useState(committed);
  const [prev, setPrev] = useState(committed);
  if (committed !== prev) {
    setPrev(committed);
    if (committed !== text.trim()) setText(committed);
  }
  const commitRef = useRef(commit);
  commitRef.current = commit;
  useEffect(() => {
    if (text.trim() === committed) return undefined;
    const t = setTimeout(() => commitRef.current(text.trim()), ms);
    return () => clearTimeout(t);
  }, [text, committed, ms]);
  return [text, setText];
}
